import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
  };
};

const showAxiosError = (error: any, fallback: string) => {
  const detail = error?.response?.data?.detail || error?.message || fallback;
  toast.error(detail);
};

export interface MetaIntegrationStatus {
  client_id: string;
  access_token: string;
  ad_account_id: string | null;
  api_base_url: string;
  api_version: string;
  page_id: string;
  page_access_token: string;
  token_expiry: string;
  active: boolean;
  app_id: string;
  app_secret: string;
}

export interface LeadForm {
  id: string;
  locale: string;
  name: string;
  status: string;
}

export interface SyncResponse {
  message: string;
}

export interface LeadDataResponse {
  leads_fetched: number;
  contacts_created: number;
}

// POST: Create Meta Integration
export const createMetaIntegration = async (
  access_token: string,
  ad_account_id: string,
  api_base_url: string,
  api_version: string,
  app_id: string,
  app_secret: string
): Promise<string> => {
  try {
    const formData = new URLSearchParams();
    formData.append("access_token", access_token);
    formData.append("ad_account_id", ad_account_id);
    formData.append("api_base_url", api_base_url);
    formData.append("api_version", api_version);
    formData.append("app_id", app_id);
    formData.append("app_secret", app_secret);

    const response = await axios.post<string>(
      `${API_BASE_PATH}/integration/meta`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    toast.success("Meta integration created successfully");
    return response.data;
  } catch (error) {
    showAxiosError(error, "Failed to create Meta integration");
    throw error;
  }
};

// PUT: Update Meta Integration
export const updateMetaIntegration = async (
  access_token: string,
  ad_account_id: string,
  api_base_url: string,
  api_version: string,
  app_id: string,
  app_secret: string
): Promise<string> => {
  try {
    const formData = new URLSearchParams();
    formData.append("access_token", access_token);
    formData.append("ad_account_id", ad_account_id);
    formData.append("api_base_url", api_base_url);
    formData.append("api_version", api_version);
    formData.append("app_id", app_id);
    formData.append("app_secret", app_secret);

    const response = await axios.put<string>(
      `${API_BASE_PATH}/integration/meta`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    toast.success("Meta integration updated successfully");
    return response.data;
  } catch (error) {
    showAxiosError(error, "Failed to update Meta integration");
    throw error;
  }
};

// GET: Get Meta Integration
export const getMetaIntegration = async (): Promise<MetaIntegrationStatus> => {
  try {
    const response = await axios.get<MetaIntegrationStatus>(
      `${API_BASE_PATH}/integration/meta`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch Meta integration");
    throw error;
  }
};

// GET: Get Meta Integration Status
export const getMetaIntegrationStatus = async (): Promise<MetaIntegrationStatus> => {
  try {
    const response = await axios.get<MetaIntegrationStatus>(
      `${API_BASE_PATH}/integration/meta/status`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch Meta integration status");
    throw error;
  }
};

// GET: Fetch Page Lead Data
export const fetchPageLeadData = async (): Promise<LeadDataResponse> => {
  try {
    const response = await axios.get<LeadDataResponse>(
      `${API_BASE_PATH}/integration/meta/leads/data`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch page lead data");
    throw error;
  }
};

// POST: Sync Contacts From Meta
export const syncContactsFromMeta = async (): Promise<SyncResponse> => {
  try {
    const response = await axios.post<SyncResponse>(
      `${API_BASE_PATH}/sync-from-meta`,
      {},
      {
        headers: getAuthHeaders(),
      }
    );
    toast.success(response.data.message);
    return response.data;
  } catch (error) {
    showAxiosError(error, "Failed to sync contacts from Meta");
    throw error;
  }
};

// GET: Fetch Page Leads
export const fetchPageLeads = async (): Promise<{ lead_forms: LeadForm[] }> => {
  try {
    const response = await axios.get<{ lead_forms: LeadForm[] }>(
      `${API_BASE_PATH}/integration/meta/leads`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch page leads");
    throw error;
  }
};

// GET: Sync Leads All Pages
export const syncLeadsAllPages = async (): Promise<string> => {
  try {
    const response = await axios.get<string>(
      `${API_BASE_PATH}/integration/meta/leads/all-pages`,
      {
        headers: getAuthHeaders(),
      }
    );
    toast.success("Leads synced successfully");
    return response.data;
  } catch (error) {
    showAxiosError(error, "Failed to sync leads from all pages");
    throw error;
  }
};